import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { findService, SERVICES, CITIES, BUSINESS } from "@/lib/site-data";
import { AnswerBlock } from "@/components/AnswerBlock";

export const Route = createFileRoute("/services/$service")({
  loader: ({ params }) => {
    const service = findService(params.service);
    if (!service) throw notFound();
    return { service };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Service not found" }] };
    }
    const s = loaderData.service;
    const title = `${s.name} | ${BUSINESS.shortName}`;
    const description = `${s.tagline} ${s.description.slice(0, 110)}`.slice(0, 158);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: `/services/${params.service}` },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: `https://lehitradessaeo.com/services/${params.service}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: s.name,
            serviceType: s.short,
            description: s.description,
            provider: {
              "@type": "ProfessionalService",
              name: BUSINESS.name,
              telephone: BUSINESS.phone,
            },
            areaServed: CITIES.map((c) => ({
              "@type": "City",
              name: `${c.name}, ${c.region}`,
            })),
            audience: {
              "@type": "BusinessAudience",
              audienceType: "Trades and home-service contractors (HVAC, plumbing, electrical, roofing, remodeling)",
            },
            serviceOutput: "Booked jobs and qualified contractor leads",
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: `${s.name} deliverables`,
              itemListElement: s.jobs.map((j: string) => ({
                "@type": "Offer",
                itemOffered: { "@type": "Service", name: j },
              })),
            },
            offers: {
              "@type": "Offer",
              priceCurrency: "USD",
              priceSpecification: { "@type": "PriceSpecification", priceCurrency: "USD" },
              availability: "https://schema.org/InStock",
              url: `https://lehitradessaeo.com/services/${params.service}`,
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: s.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-3xl font-black">Service not found</h1>
      <p className="mt-2 text-muted-foreground">
        We don't have that service listed. <Link to="/services" className="underline">See all services</Link>.
      </p>
    </main>
  ),
  component: ServicePage,
});

function ServicePage() {
  const { service } = Route.useLoaderData();
  const others = SERVICES.filter((s) => s.slug !== service.slug);
  return (
    <main>
      <section className="border-b border-border">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            {service.short}
          </p>
          <h1 className="mt-4 font-display text-5xl tracking-tight md:text-6xl">
            {service.name}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
            {service.tagline}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Get a free audit
            </Link>
            <a
              href={BUSINESS.phoneHref}
              className="rounded-full px-6 py-3 text-sm font-medium text-foreground hover:bg-secondary"
            >
              Call {BUSINESS.phone}
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16">
        <AnswerBlock
          question={`What does ${service.name} actually include?`}
          answer={service.tagline}
          bullets={service.jobs.slice(0, 4)}
        />
        <div className="grid gap-12 md:grid-cols-3">
          <article className="md:col-span-2">
            <h2 className="font-display text-3xl tracking-tight">What we handle</h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">{service.description}</p>
            <ul className="mt-6 grid gap-2 sm:grid-cols-2">
              {service.jobs.map((j: string) => (
                <li key={j} className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 text-sm">
                  <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  {j}
                </li>
              ))}
            </ul>

            <h2 className="mt-16 font-display text-3xl tracking-tight">
              Frequently asked questions
            </h2>
            <div className="mt-6 divide-y divide-border border-y border-border">
              {service.faqs.map((f: { q: string; a: string }) => (
                <details key={f.q} className="group py-5">
                  <summary className="cursor-pointer list-none font-medium">
                    <span className="mr-2 text-primary">+</span>
                    {f.q}
                  </summary>
                  <p className="mt-3 pl-6 text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </article>

          <aside className="space-y-6">
            <div className="rounded-2xl border border-border bg-card p-6">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Talk to us</p>
              <p className="mt-3 font-display text-2xl">{BUSINESS.phone}</p>
              <p className="mt-1 text-sm text-muted-foreground">{BUSINESS.hours}</p>
            </div>
            <div>
              <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                Service areas
              </h3>
              <ul className="mt-3 space-y-1.5 text-sm">
                {CITIES.map((c) => (
                  <li key={c.slug}>
                    <Link to="/service-areas/$city" params={{ city: c.slug }} className="text-muted-foreground hover:text-foreground">
                      {service.short} in {c.name}, {c.region}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                Other services
              </h3>
              <ul className="mt-3 space-y-1.5 text-sm">
                {others.map((o) => (
                  <li key={o.slug}>
                    <Link to="/services/$service" params={{ service: o.slug }} className="text-muted-foreground hover:text-foreground">
                      {o.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}